"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsPixelManagement = exports.facebookAdsPixelManagementArgsSchema = exports.FacebookAdsPixelManagementAction = exports.facebookAdsGetPixelStatsResultSchema = exports.facebookAdsGetPixelStatsArgsSchema = exports.facebookAdsUpdatePixelResultSchema = exports.facebookAdsUpdatePixelArgsSchema = exports.facebookAdsCreatePixelResultSchema = exports.facebookAdsCreatePixelArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Pixel ----------
exports.facebookAdsCreatePixelArgsSchema = zod_1.z.object({
    name: zod_1.z.string().describe('Pixel name'),
});
exports.facebookAdsCreatePixelResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Pixel ID'),
    name: zod_1.z.string(),
    created: zod_1.z.boolean().default(true),
});
// ---------- Update Pixel ----------
exports.facebookAdsUpdatePixelArgsSchema = zod_1.z.object({
    pixelId: zod_1.z.string().describe('Pixel ID to update'),
    name: zod_1.z.string().optional().nullable().describe('New pixel name'),
});
exports.facebookAdsUpdatePixelResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Pixel ID'),
    updated: zod_1.z.boolean().default(true),
    success: zod_1.z.boolean(),
});
// ---------- Get Pixel Stats ----------
exports.facebookAdsGetPixelStatsArgsSchema = zod_1.z.object({
    pixelId: zod_1.z.string().describe('Facebook Pixel ID'),
    startTime: zod_1.z.number().optional().nullable().describe('Start time as Unix timestamp'),
    endTime: zod_1.z.number().optional().nullable().describe('End time as Unix timestamp'),
    aggregation: zod_1.z
        .enum([
        'event',
        'browser_type',
        'custom_data_field',
        'device_os',
        'device_type',
        'host',
        'url',
        'pixel_fire',
        'event_total_counts',
    ])
        .optional()
        .nullable()
        .describe('Aggregation type'),
    includeRevenueData: zod_1.z
        .boolean()
        .optional()
        .nullable()
        .describe('Include revenue data from Ads Insights API. Shows ad-attributed conversions with revenue values and calculates ROAS. Note: Only includes conversions within ad attribution window (7-day click / 1-day view).'),
});
exports.facebookAdsGetPixelStatsResultSchema = zod_1.z.object({
    pixel_id: zod_1.z.string().describe('Pixel ID'),
    pixel_name: zod_1.z.string().optional().nullable().describe('Pixel name'),
    stats: zod_1.z
        .array(zod_1.z.object({
        aggregation: zod_1.z.string(),
        count: zod_1.z.number(),
        timestamp: zod_1.z.number().optional().nullable(),
        value: zod_1.z.number().optional().nullable().describe('Monetary value for this aggregation'),
    }))
        .describe('Statistics data'),
    total_events: zod_1.z.number().describe('Total number of events'),
    event_types: zod_1.z
        .array(zod_1.z.object({
        event_name: zod_1.z.string(),
        count: zod_1.z.number(),
    }))
        .describe('Event types breakdown'),
    time_range: zod_1.z
        .object({
        start: zod_1.z.number().optional().nullable(),
        end: zod_1.z.number().optional().nullable(),
    })
        .describe('Time range of stats'),
    revenue_data: zod_1.z
        .object({
        ad_attributed_conversions: zod_1.z
            .array(zod_1.z.object({
            action_type: zod_1.z.string().describe('Conversion type (e.g., purchase, add_to_cart)'),
            count: zod_1.z.number().describe('Number of conversions'),
            value: zod_1.z.number().describe('Total revenue or value for this action type'),
        }))
            .describe('Ad-attributed conversions with revenue values'),
        total_ad_spend: zod_1.z
            .number()
            .describe('Total ad spend for campaigns using this pixel in the time period'),
        total_attributed_revenue: zod_1.z.number().describe('Total revenue from ad-attributed conversions'),
        roas: zod_1.z.number().optional().nullable().describe('Return on ad spend (revenue / spend)'),
        attribution_coverage: zod_1.z
            .number()
            .optional()
            .nullable()
            .describe('Percentage of pixel events that have ad attribution (0-100)'),
        campaigns_using_pixel: zod_1.z
            .array(zod_1.z.object({
            campaign_id: zod_1.z.string(),
            campaign_name: zod_1.z.string(),
            spend: zod_1.z.number(),
            revenue: zod_1.z.number(),
            conversions: zod_1.z.number(),
            roas: zod_1.z.number(),
        }))
            .optional()
            .nullable()
            .describe('Breakdown by campaign'),
    })
        .optional()
        .nullable()
        .describe('Revenue data from Ads Insights API. Only available when includeRevenueData is true. Note: Only includes ad-attributed conversions within attribution window.'),
});
// ==========================
// Compound Tool: facebookAdsPixelManagement
// ==========================
exports.FacebookAdsPixelManagementAction = zod_1.z.enum([
    'createPixel',
    'updatePixel',
    'getPixelStats',
]);
exports.facebookAdsPixelManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createPixel'), params: exports.facebookAdsCreatePixelArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updatePixel'), params: exports.facebookAdsUpdatePixelArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('getPixelStats'), params: exports.facebookAdsGetPixelStatsArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var FacebookAdsPixelManagementAllResultsSchema = zod_1.z.union([
    exports.facebookAdsCreatePixelResultSchema,
    exports.facebookAdsUpdatePixelResultSchema,
    exports.facebookAdsGetPixelStatsResultSchema,
]);
var facebookAdsPixelManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createPixel' }, schema: exports.facebookAdsCreatePixelResultSchema },
        { when: { 'action.name': 'updatePixel' }, schema: exports.facebookAdsUpdatePixelResultSchema },
        { when: { 'action.name': 'getPixelStats' }, schema: exports.facebookAdsGetPixelStatsResultSchema },
    ],
    defaultSchema: FacebookAdsPixelManagementAllResultsSchema,
});
exports.facebookAdsPixelManagement = (0, common_1.createToolMetadata)({
    id: 'facebookAdsPixelManagement',
    displayName: 'Facebook Pixel Management',
    displayDescription: 'Create, update, and get statistics for Facebook Ads pixels',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsPixelManagementArgsSchema,
}, facebookAdsPixelManagementResultSchemas);
